import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Info,
  Users,
  Video,
} from "lucide-react";
import { Fragment, useState } from "react";
import { TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { Card } from "@/components/ui/card";
import { Id } from "@cvx/_generated/dataModel";
import { Link } from "@tanstack/react-router";
import { Tooltip as UITooltip } from "@/components/ui/tooltip";
import { formatNumber } from "@/utils/format";

export interface VideoProjectStat {
  creatorId: Id<"creators">;
  creatorName: string;
  avatarUrl: string | null;
  totalProjects: number;
  teamReviewing: number;
  brandReviewing: number;
  needsCreatorReview: number;
  published: number;
  brandCounts: Array<{
    appId: Id<"apps">;
    appName: string;
    totalProjects: number;
    teamReviewing: number;
    brandReviewing: number;
    needsCreatorReview: number;
    published: number;
  }>;
}

type SortColumn =
  | "creatorName"
  | "totalProjects"
  | "teamReviewing"
  | "brandReviewing"
  | "needsCreatorReview"
  | "published";

interface VideoProjectStatsTableProps {
  isLoading: boolean;
  stats?: VideoProjectStat[];
}

export function VideoProjectStatsTable({
  isLoading,
  stats,
}: VideoProjectStatsTableProps) {
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [expandedCreatorIds, setExpandedCreatorIds] = useState<
    Set<Id<"creators">>
  >(new Set());

  if (isLoading) {
    return (
      <Card className="p-6 min-h-[400px]">
        <div className="h-48 bg-gray-100 rounded animate-pulse flex items-center justify-center">
          <div className="text-primary/60">Loading video project stats...</div>
        </div>
      </Card>
    );
  }

  if (!stats || stats.length === 0) {
    return (
      <Card className="p-6 min-h-[400px]">
        <div className="text-center py-12">
          <Video className="h-12 w-12 text-primary/40 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-primary mb-2">
            No Video Projects Found
          </h3>
          <p className="text-sm text-primary/60">
            No video projects have been submitted by the selected creators yet.
          </p>
        </div>
      </Card>
    );
  }

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) {
      return <ArrowUp className="h-3 w-3 ml-1 opacity-30" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    );
  };

  const sortedData = [...stats].sort((a, b) => {
    if (!sortColumn) return 0;
    let aVal: string | number;
    let bVal: string | number;
    if (sortColumn === "creatorName") {
      aVal = a.creatorName.toLowerCase();
      bVal = b.creatorName.toLowerCase();
    } else {
      aVal = a[sortColumn];
      bVal = b[sortColumn];
    }
    if (sortDirection === "asc") {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  const totals = sortedData.reduce(
    (acc, stat) => ({
      totalProjects: acc.totalProjects + stat.totalProjects,
      teamReviewing: acc.teamReviewing + stat.teamReviewing,
      brandReviewing: acc.brandReviewing + stat.brandReviewing,
      needsCreatorReview: acc.needsCreatorReview + stat.needsCreatorReview,
      published: acc.published + stat.published,
    }),
    {
      totalProjects: 0,
      teamReviewing: 0,
      brandReviewing: 0,
      needsCreatorReview: 0,
      published: 0,
    },
  );

  return (
    <Card className="p-6 min-h-[400px]">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Video className="h-5 w-5 text-primary/70" />
          <h3 className="text-lg font-semibold text-primary">
            Video Project Status by Creator
          </h3>
          <UITooltip>
            <TooltipTrigger>
              <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">
                Shows the current status of video projects submitted by each
                creator across all their campaigns.
              </p>
            </TooltipContent>
          </UITooltip>
        </div>
        <p className="text-sm text-primary/60 mb-4">
          Track video project submissions and their approval status
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="text-left py-3 px-4 font-semibold text-primary">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleSort("creatorName")}
                    className="flex items-center hover:opacity-80"
                  >
                    Creator
                    <SortIcon column="creatorName" />
                  </button>
                </div>
              </th>
              <th className="text-right py-3 px-4 font-semibold text-primary">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => handleSort("totalProjects")}
                    className="flex items-center hover:opacity-80"
                  >
                    Total Projects
                    <SortIcon column="totalProjects" />
                  </button>
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Total number of video projects submitted by this creator
                        across all campaigns.
                      </p>
                    </TooltipContent>
                  </UITooltip>
                </div>
              </th>
              <th className="text-right py-3 px-4 font-semibold text-primary">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => handleSort("teamReviewing")}
                    className="flex items-center hover:opacity-80"
                  >
                    Team Reviewed
                    <SortIcon column="teamReviewing" />
                  </button>
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Number of video projects that have been reviewed by the
                        team and received feedback (approved or rejected
                        status).
                      </p>
                    </TooltipContent>
                  </UITooltip>
                </div>
              </th>
              <th className="text-right py-3 px-4 font-semibold text-primary">
                <div className="flex items-center justify-end gap-2">
                  <span className="text-sm text-primary/70">% to Team</span>
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Percentage of total projects that reached team review
                        stage (Team Reviewed / Total Projects).
                      </p>
                    </TooltipContent>
                  </UITooltip>
                </div>
              </th>
              <th className="text-right py-3 px-4 font-semibold text-primary">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => handleSort("needsCreatorReview")}
                    className="flex items-center hover:opacity-80"
                  >
                    Needs Creator Review
                    <SortIcon column="needsCreatorReview" />
                  </button>
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Number of video projects that have been rejected and
                        need the creator to revise (team_rejected or rejected
                        status).
                      </p>
                    </TooltipContent>
                  </UITooltip>
                </div>
              </th>
              <th className="text-right py-3 px-4 font-semibold text-primary">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => handleSort("brandReviewing")}
                    className="flex items-center hover:opacity-80"
                  >
                    Brand Reviewed
                    <SortIcon column="brandReviewing" />
                  </button>
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Number of video projects that have been reviewed by the
                        brand and received feedback (approved or rejected
                        status).
                      </p>
                    </TooltipContent>
                  </UITooltip>
                </div>
              </th>
              <th className="text-right py-3 px-4 font-semibold text-primary">
                <div className="flex items-center justify-end gap-2">
                  <span className="text-sm text-primary/70">% to Brand</span>
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Percentage of team reviewed projects that reached brand
                        review stage (Brand Reviewed / Team Reviewed).
                      </p>
                    </TooltipContent>
                  </UITooltip>
                </div>
              </th>
              <th className="text-right py-3 px-4 font-semibold text-primary">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => handleSort("published")}
                    className="flex items-center hover:opacity-80"
                  >
                    Published
                    <SortIcon column="published" />
                  </button>
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Number of video projects that have been published to
                        social media platforms (linked to an approved contract
                        video).
                      </p>
                    </TooltipContent>
                  </UITooltip>
                </div>
              </th>
              <th className="text-right py-3 px-4 font-semibold text-primary">
                <div className="flex items-center justify-end gap-2">
                  <span className="text-sm text-primary/70">% Published</span>
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Percentage of brand reviewed projects that were
                        published (Published / Brand Reviewed).
                      </p>
                    </TooltipContent>
                  </UITooltip>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((stat) => (
              <Fragment key={stat.creatorId}>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-3 px-4 text-left text-primary">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-3">
                        {stat.avatarUrl ? (
                          <img
                            src={stat.avatarUrl}
                            alt={stat.creatorName}
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                            <Users className="h-4 w-4 text-primary/60" />
                          </div>
                        )}
                        <Link
                          to="/creators/$creatorId"
                          params={{ creatorId: stat.creatorId }}
                          className="font-medium hover:underline text-primary"
                        >
                          {stat.creatorName}
                        </Link>
                      </div>
                      {stat.brandCounts && stat.brandCounts.length > 0 && (
                        <button
                          onClick={() => {
                            setExpandedCreatorIds((prev) => {
                              const newSet = new Set(prev);
                              if (newSet.has(stat.creatorId)) {
                                newSet.delete(stat.creatorId);
                              } else {
                                newSet.add(stat.creatorId);
                              }
                              return newSet;
                            });
                          }}
                          className="text-xs text-primary/60 hover:text-primary/80 flex items-center gap-1 ml-11 transition-colors"
                        >
                          {expandedCreatorIds.has(stat.creatorId)
                            ? "Hide"
                            : "View"}{" "}
                          by brand
                          <ChevronDown
                            className={`h-3 w-3 transition-transform ${
                              expandedCreatorIds.has(stat.creatorId)
                                ? "rotate-180"
                                : ""
                            }`}
                          />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right text-primary">
                    {formatNumber(stat.totalProjects)}
                  </td>
                  <td className="py-3 px-4 text-right text-primary">
                    {formatNumber(stat.teamReviewing)}
                  </td>
                  <td className="py-3 px-4 text-right text-primary text-sm">
                    {stat.totalProjects > 0
                      ? `${Math.round(
                          (stat.teamReviewing / stat.totalProjects) * 100,
                        )}%`
                      : "-"}
                  </td>
                  <td className="py-3 px-4 text-right text-primary">
                    {formatNumber(stat.needsCreatorReview)}
                  </td>
                  <td className="py-3 px-4 text-right text-primary">
                    {formatNumber(stat.brandReviewing)}
                  </td>
                  <td className="py-3 px-4 text-right text-primary text-sm">
                    {stat.teamReviewing > 0
                      ? `${Math.round(
                          (stat.brandReviewing / stat.teamReviewing) * 100,
                        )}%`
                      : "-"}
                  </td>
                  <td className="py-3 px-4 text-right text-primary">
                    {formatNumber(stat.published)}
                  </td>
                  <td className="py-3 px-4 text-right text-primary text-sm">
                    {stat.brandReviewing > 0
                      ? `${Math.round(
                          (stat.published / stat.brandReviewing) * 100,
                        )}%`
                      : "-"}
                  </td>
                </tr>
                {expandedCreatorIds.has(stat.creatorId) &&
                  stat.brandCounts &&
                  stat.brandCounts.length > 0 &&
                  stat.brandCounts.map((brand) => (
                    <tr
                      key={`${stat.creatorId}-${brand.appId}`}
                      className="border-b border-gray-100 dark:border-gray-700 bg-secondary/30"
                    >
                      <td className="py-2 px-4 text-left text-primary pl-12">
                        <span className="text-sm text-primary/70">
                          └ {brand.appName}
                        </span>
                      </td>
                      <td className="py-2 px-4 text-right text-primary text-sm">
                        {formatNumber(brand.totalProjects)}
                      </td>
                      <td className="py-2 px-4 text-right text-primary text-sm">
                        {formatNumber(brand.teamReviewing)}
                      </td>
                      <td className="py-2 px-4 text-right text-primary text-sm">
                        {brand.totalProjects > 0
                          ? `${Math.round(
                              (brand.teamReviewing / brand.totalProjects) * 100,
                            )}%`
                          : "-"}
                      </td>
                      <td className="py-2 px-4 text-right text-primary text-sm">
                        {formatNumber(brand.needsCreatorReview)}
                      </td>
                      <td className="py-2 px-4 text-right text-primary text-sm">
                        {formatNumber(brand.brandReviewing)}
                      </td>
                      <td className="py-2 px-4 text-right text-primary text-sm">
                        {brand.teamReviewing > 0
                          ? `${Math.round(
                              (brand.brandReviewing / brand.teamReviewing) *
                                100,
                            )}%`
                          : "-"}
                      </td>
                      <td className="py-2 px-4 text-right text-primary text-sm">
                        {formatNumber(brand.published)}
                      </td>
                      <td className="py-2 px-4 text-right text-primary text-sm">
                        {brand.brandReviewing > 0
                          ? `${Math.round(
                              (brand.published / brand.brandReviewing) * 100,
                            )}%`
                          : "-"}
                      </td>
                    </tr>
                  ))}
              </Fragment>
            ))}
            <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-secondary/50 font-semibold">
              <td className="py-3 px-4 text-left text-primary">Total</td>
              <td className="py-3 px-4 text-right text-primary">
                {formatNumber(totals.totalProjects)}
              </td>
              <td className="py-3 px-4 text-right text-primary">
                {formatNumber(totals.teamReviewing)}
              </td>
              <td className="py-3 px-4 text-right text-primary text-sm">
                {totals.totalProjects > 0
                  ? `${Math.round(
                      (totals.teamReviewing / totals.totalProjects) * 100,
                    )}%`
                  : "-"}
              </td>
              <td className="py-3 px-4 text-right text-primary">
                {formatNumber(totals.needsCreatorReview)}
              </td>
              <td className="py-3 px-4 text-right text-primary">
                {formatNumber(totals.brandReviewing)}
              </td>
              <td className="py-3 px-4 text-right text-primary text-sm">
                {totals.teamReviewing > 0
                  ? `${Math.round(
                      (totals.brandReviewing / totals.teamReviewing) * 100,
                    )}%`
                  : "-"}
              </td>
              <td className="py-3 px-4 text-right text-primary">
                {formatNumber(totals.published)}
              </td>
              <td className="py-3 px-4 text-right text-primary text-sm">
                {totals.brandReviewing > 0
                  ? `${Math.round(
                      (totals.published / totals.brandReviewing) * 100,
                    )}%`
                  : "-"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}
